import { z } from 'zod'
import type { PauzeBronKey } from './pauze-tweeassig'

/**
 * batch-69 werkbon-pauze (FO-511) - gedeelde vormen voor de klantpauze: de
 * factuurpauze die naar de klant of Finqle gaat is altijd een KLANTREGEL. De
 * reken- en beoordelingslogica staat in `@streep/utils` (`werkbon-pauze.ts`) en
 * de api (`lib/klantpauze.ts`); hier alleen de vormen die backoffice en api
 * delen.
 */

/** De foutcode van de verzend-gate (single, bulk, bundel, Finqle-API, klant-mail). */
export const KLANTPAUZE_GATE_CODE = 'KLANTPAUZE_ONBEVESTIGD' as const

/**
 * De bronModule van het KLANTPAUZE_ONBEVESTIGD-signaal (`NotificatieEvent.module`,
 * dus ook `InboxItem.bronModule`). Bewust NIET 'werkbonnen': de werkbon-resolver
 * van de inbox-reconciliatie sluit items op de goedkeuringsstatus van de bon, en
 * een verzonden bon is altijd goedgekeurd. Deze module heeft een eigen sluitregel
 * (de klantpauze van de werkbon geldt).
 */
export const KLANTPAUZE_BRON_MODULE = 'klantpauze' as const

/**
 * Waarom de factuurpauze van een dag (nog) niet naar de klant mag:
 *   - GEEN_KLANTREGEL: het werkschema kent voor deze dienst geen factuurpauze en
 *     geen klant-standaard (of de bon hangt aan geen werkschema);
 *   - NIET_BEVESTIGD: er is een afspraak, maar het werkschema is niet bevestigd;
 *   - VOOR_INGANGSDATUM: de werkdatum ligt vóór de ingangsdatum van de afspraken;
 *   - AFWIJKEND: de klantregel is bevestigd, maar de dagregel draagt een andere
 *     factuurpauze (niet herrekend sinds de regel wijzigde of bevestigd werd);
 *   - WERKSCHEMA_VERWIJDERD: het werkschema van de bon is weg en de klant heeft
 *     geen eenduidig ander rooster.
 */
export const KLANTPAUZE_PROBLEEM_KEYS = [
  'GEEN_KLANTREGEL',
  'NIET_BEVESTIGD',
  'VOOR_INGANGSDATUM',
  'AFWIJKEND',
  'WERKSCHEMA_VERWIJDERD',
] as const
export type KlantpauzeProbleemKey = (typeof KLANTPAUZE_PROBLEEM_KEYS)[number]

export const KLANTPAUZE_PROBLEEM_LABELS: Record<KlantpauzeProbleemKey, string> = {
  GEEN_KLANTREGEL: 'geen pauzeregel van de klant voor deze dienst',
  NIET_BEVESTIGD: 'pauzeregel van de klant nog niet bevestigd',
  VOOR_INGANGSDATUM: 'pauzeregel van de klant geldt pas vanaf een latere datum',
  AFWIJKEND: 'factuurpauze wijkt af van de bevestigde pauzeregel (nog niet herrekend)',
  WERKSCHEMA_VERWIJDERD: 'het werkschema van deze werkbon is verwijderd',
}

/** Eén dag waarvan de factuurpauze (nog) geen geldende klantregel is. */
export interface KlantpauzeDagOordeelDTO {
  dayEntryId: string
  /** yyyy-MM-dd */
  datum: string
  /** "22:00-06:00" */
  tijden: string
  /** De factuurpauze zoals de dag nu gefactureerd zou worden (minuten). */
  factuurpauzeMinuten: number
  /**
   * De herkomst van de factuurpauze op de dagregel (`null`: oude dagregel zonder eigen
   * factuurpauze). `SYSTEEM_DEFAULT` = aangenomen zonder pauzeregel van de klant.
   */
  factuurpauzeBron: PauzeBronKey | null
  /** De factuurpauze volgens de klantregel (of het voorstel), in minuten. */
  klantregelMinuten: number
  klantregelBron: PauzeBronKey
  probleem: KlantpauzeProbleemKey
  /** De facturabele uren nu en met de factuurpauze van de klantregel. */
  factuurUren: number
  factuurUrenVolgensKlantregel: number
  /**
   * Integrator-review H3 - de factuurpauze die uit de pauze van de monteur volgt
   * (de stand van vóór FO-511: de geschreven pauze, 0 bij een doorbetaalde pauze).
   * Zo noemen de waarschuwing en het signaal wat een aangenomen waarde verving.
   * Een dag zonder eigen factuurpauze (de oude stand) factureert al zo: dan gelijk
   * aan `factuurpauzeMinuten` (er is niets aangenomen).
   */
  afgeleideFactuurpauzeMinuten: number
}

/**
 * Waar de werkbon in de facturatie staat; bepaalt de weg eruit (review M1):
 *   - OPEN: nog nergens in - herrekenen op de werkbon;
 *   - KLANTAKKOORD (integrator-review M2): de klant beoordeelt de uren nu
 *     (WACHT_OP_KLANT) of heeft ze geaccordeerd: de factuurpauze gaat niet zonder
 *     nieuw akkoord omhoog of omlaag. Herrekenen zet de dag op de correctielijst;
 *     per dag de geaccordeerde factuurpauze vastleggen (met reden) kan wel;
 *   - FACTURATIEVERZOEK: naar Finqle verzonden (ook via de Finqle-API, nog zonder
 *     lokaal verzoek), het verzoek staat nog open - corrigeren via de lijst
 *     "pauze-twijfel" en opnieuw aanleveren;
 *   - FACTURATIE_OVERZICHT: in een lopend facturatie-overzicht - dat eerst
 *     annuleren, dan herrekenen en het overzicht opnieuw genereren;
 *   - FACTUUR_NIET_VERZONDEN (review 2): op een eigen factuur die nog niet de deur
 *     uit is (concept, of een nummer dat nooit verzonden is en niet in het
 *     klantportaal staat) - die factuur verwijderen, herrekenen en opnieuw maken;
 *   - GEFACTUREERD: definitief (een verzonden eigen factuur, Finqle-factuur,
 *     handmatig, of een verzoek dat de klant al accordeerde): een correctie is
 *     crediteren of herfactureren. Niet meer corrigeerbaar in STREEP.
 */
export const KLANTPAUZE_FACTURATIE_SLOTS = [
  'OPEN',
  'KLANTAKKOORD',
  'FACTURATIEVERZOEK',
  'FACTURATIE_OVERZICHT',
  'FACTUUR_NIET_VERZONDEN',
  'GEFACTUREERD',
] as const
export type KlantpauzeFacturatieSlot = (typeof KLANTPAUZE_FACTURATIE_SLOTS)[number]

/** De beoordeling van één werkbon: mag de factuurpauze naar de klant of Finqle? */
export interface KlantpauzeBeoordelingDTO {
  werkbonId: string
  werkbonNumber: string
  clientId: string
  clientNaam: string
  workScheduleId: string | null
  /** FO-008: een besloten bon (nummer en klant blijven uit het signaal). */
  besloten: boolean
  /** Elke dag met uren heeft een geldende klantpauze (of een handmatige correctie). */
  ok: boolean
  /** Alleen de dagen met een probleem. */
  dagen: KlantpauzeDagOordeelDTO[]
  /** Leesbare uitleg ("Op 3 dagen: ..."); leeg als `ok`. */
  samenvatting: string
  /** Waar de werkbon in de facturatie staat (de weg eruit hangt ervan af). */
  facturatieSlot: KlantpauzeFacturatieSlot
  /** De eigen factuur van de werkbon (review 2: de weg eruit wijst ernaar). */
  invoiceId: string | null
  invoiceNumber: string | null
  /**
   * Review 2 (MIDDEL-4) - de factuurpauze is gecorrigeerd voor herlevering, maar
   * de werkbon is daarna nog niet opnieuw naar Finqle verzonden.
   */
  herleveringOpen: boolean
}

/**
 * Review 2 (MIDDEL-5) - wat de gate raakt zodra hij aan gaat, per stap. Alleen
 * werkbonnen zonder geldende klantpauze.
 */
export interface KlantpauzeGateTellingDTO {
  /**
   * Intern goedgekeurd of verder, nog niet verzonden of gefactureerd: gaan niet
   * naar Finqle of de klant, en er wordt geen eigen factuur van gemaakt.
   */
  versturen: number
  /**
   * Wachten op interne goedkeuring en gaan daarna naar de klant voor akkoord:
   * worden wel intern goedgekeurd (de verloning loopt door), de klantstap wacht.
   */
  klantakkoord: number
  /** Nu al intern goedgekeurd met een klantstap die op de pauzeregel wacht. */
  vastgehouden: number
  /** Peilmoment van de telling (ISO); hij wordt een paar minuten bewaard. */
  berekendOp: string
}

/** Stand van de verzend-gate (Relaties - Pauze-instellingen). */
export interface KlantpauzeGateStandDTO {
  /** Weigert de verzend-gate een werkbon zonder geldende klantpauze? */
  actief: boolean
  /** Werkschema's die nog niet (compleet) bevestigd zijn. */
  aantalTeBevestigen: number
  /**
   * Review M2 - werkbonnen die klaar staan om te versturen en nu geen geldende
   * klantpauze hebben: wat de gate zou weigeren zodra hij aan gaat (ook na
   * bevestigen, zolang de dagen niet herrekend zijn). Gelijk aan `telling.versturen`.
   */
  aantalWerkbonnenGeweigerd: number
  /** Review 2 (MIDDEL-5) - de telling per stap. */
  telling: KlantpauzeGateTellingDTO
}

export const klantpauzeGateSchema = z.object({
  actief: z.boolean({ message: 'Kies of de verzend-gate aan of uit staat' }),
})
export type KlantpauzeGateInput = z.infer<typeof klantpauzeGateSchema>

// ─── Pauzeregels te bevestigen (bulk) ───────────────────────────────────────

/**
 * Bulk-bevestigen van de pauzeregels (spec §B4): per werkschema de diensten
 * zonder factuurpauze vullen met het voorstel (oude schema-brede waarde of
 * systeemstandaard) en het schema als bevestigd markeren. Alleen schema's die
 * de aanroeper expliciet aanvinkt.
 */
export const pauzeregelsBevestigenSchema = z.object({
  workScheduleIds: z
    .array(z.string().min(1))
    .min(1, 'Kies minstens één werkschema om te bevestigen')
    .max(200, 'Bevestig maximaal 200 werkschema’s per keer'),
  /**
   * Review M3 - bevestigen zet `pauzeBevestigdOp` voor het hele schema, dus ook
   * voor de pauze en verloning (FO-423). Diensten waarvan die nog niet gekozen is,
   * worden alleen meebevestigd als de gebruiker dat expliciet aangeeft; anders
   * wordt zo'n werkschema overgeslagen met de reden.
   */
  ookOpenPauzevragen: z.boolean().optional(),
})
export type PauzeregelsBevestigenInput = z.infer<typeof pauzeregelsBevestigenSchema>

export interface PauzeregelsBevestigenResultaatDTO {
  bevestigd: Array<{ workScheduleId: string; clientNaam: string; dienstenAangevuld: string[] }>
  overgeslagen: Array<{ workScheduleId: string; reden: string }>
}

// ─── Verzoeken met pauze-twijfel (spec §C7) ─────────────────────────────────

/** Eén werkbon in een facturatieverzoek waarvan de factuurpauze twijfel geeft. */
export interface PauzeTwijfelWerkbonDTO {
  werkbonId: string
  werkbonNumber: string
  /** Dagen waar de factuurpauze afwijkt van de klantregel (of het voorstel). */
  dagen: KlantpauzeDagOordeelDTO[]
  /** Factuur-uren volgens de klantregel minus nu, over die dagen (negatief = minder uren). */
  urenVerschil: number
  /** Zijn alle klantregels van de afwijkende dagen bevestigd? */
  klantregelsBevestigd: boolean
  /**
   * Review 2 (MIDDEL-4) - gecorrigeerd, maar nog niet opnieuw aangeleverd: Finqle
   * heeft nog de oude opgave. De regel blijft in de lijst tot de herverzending.
   */
  herleveringOpen: boolean
}

export interface PauzeTwijfelVerzoekDTO {
  verzoekId: string
  /** Het nummer of de referentie van het verzoek zoals Finqle/STREEP hem kent. */
  referentie: string | null
  status: string
  clientId: string
  clientNaam: string
  werkbonnen: PauzeTwijfelWerkbonDTO[]
  /** Som van (volgens klantregel - nu) over alle werkbonnen; negatief = minder uren. */
  urenImpact: number
  /** Herrekenen voor herlevering kan (alle regels bevestigd, verzoek nog open). */
  herrekenbaar: boolean
}

export const pauzeTwijfelHerrekenSchema = z.object({
  reden: z
    .string({ message: 'Geef een reden voor de correctie' })
    .trim()
    .min(3, 'Geef een reden van minstens 3 tekens')
    .max(500, 'De reden is maximaal 500 tekens'),
})
export type PauzeTwijfelHerrekenInput = z.infer<typeof pauzeTwijfelHerrekenSchema>
